import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import EmployeeNavbar from "../Layout/EmployeeNavbar";
import TermsConditionsPopup from "../components/TermsConditionsPopup";
import { FaTasks, FaClock, FaFileAlt, FaStopwatch } from "react-icons/fa";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import {
  startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  subWeeks, parseISO, isWithinInterval,
} from "date-fns";
import API_BASE_URL from "../config";

/* ─── Injected styles ────────────────────────────────────────── */
const STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Sora:wght@600;700&display=swap');

  :root {
    --bg: #f0f4ff;
    --surface: #ffffff;
    --surface2: #f7f9ff;
    --border: #e4e9f7;
    --primary: #4f6ef7;
    --primary-light: #eef1ff;
    --accent-teal: #0dcfb4;
    --accent-amber: #f5a623;
    --accent-rose: #f25f7e;
    --accent-violet: #9b6ef7;
    --text-primary: #1a1f3c;
    --text-secondary: #5a617d;
    --text-muted: #97a0bb;
    --shadow-sm: 0 1px 4px rgba(79,110,247,0.07);
    --shadow-md: 0 4px 24px rgba(79,110,247,0.12);
    --shadow-lg: 0 12px 48px rgba(79,110,247,0.18);
    --radius: 18px;
    --radius-sm: 12px;
  }

  .emp-dash * { box-sizing: border-box; }

  html, body, #root {
    height: 100%;
    margin: 0;
    padding: 0;
    overflow: hidden;
  }

  .emp-dash {
    font-family: 'DM Sans', sans-serif;
    background: var(--bg);
    color: var(--text-primary);
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
  }

  .emp-dash__scroll {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    scroll-behavior: smooth;
    padding-top: 80px;
  }

  .emp-dash__scroll::-webkit-scrollbar { width: 6px; }
  .emp-dash__scroll::-webkit-scrollbar-track { background: transparent; }
  .emp-dash__scroll::-webkit-scrollbar-thumb {
    background: #c7d0f0;
    border-radius: 99px;
  }
  .emp-dash__scroll::-webkit-scrollbar-thumb:hover { background: var(--primary); }

  .emp-dash__inner {
    width: 100%;
    padding: 40px 40px 60px;
  }

  /* ── Header ── */
  .emp-dash__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 36px;
    flex-wrap: wrap;
    gap: 12px;
  }
  .emp-dash__greeting { margin: 0; }
  .emp-dash__greeting-sub {
    font-size: 13px;
    color: var(--text-muted);
    margin: 4px 0 0;
    font-weight: 400;
  }
  .emp-dash__title {
    font-family: 'Sora', sans-serif;
    font-size: clamp(22px, 3vw, 30px);
    font-weight: 700;
    color: var(--text-primary);
    margin: 0;
  }
  .emp-dash__title span { color: var(--primary); }

  .emp-dash__date-badge {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 999px;
    padding: 8px 18px;
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary);
    box-shadow: var(--shadow-sm);
    white-space: nowrap;
  }

  /* ── Stat Cards ── */
  .emp-dash__cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 18px;
    margin-bottom: 28px;
  }
  @media (max-width: 1024px) {
    .emp-dash__cards { grid-template-columns: repeat(2, 1fr); }
  }
  @media (max-width: 600px) {
    .emp-dash__cards { grid-template-columns: 1fr; }
  }

  .stat-card {
    background: var(--surface);
    border-radius: var(--radius);
    padding: 24px 22px;
    border: 1px solid var(--border);
    box-shadow: var(--shadow-sm);
    cursor: pointer;
    transition: transform 0.22s ease, box-shadow 0.22s ease;
    position: relative;
    overflow: hidden;
  }
  .stat-card:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);
  }
  .stat-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    border-radius: var(--radius) var(--radius) 0 0;
    background: var(--card-accent, var(--primary));
  }
  .stat-card--blue  { --card-accent: var(--primary); }
  .stat-card--teal  { --card-accent: var(--accent-teal); }
  .stat-card--green { --card-accent: #22c55e; }
  .stat-card--amber { --card-accent: var(--accent-amber); }

  .stat-card__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
  }
  .stat-card__label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--text-muted);
  }
  .stat-card__icon {
    width: 42px; height: 42px;
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    background: var(--card-bg, #eef1ff);
    flex-shrink: 0;
  }
  .stat-card--blue  .stat-card__icon { background: #eef1ff; }
  .stat-card--teal  .stat-card__icon { background: #e0faf7; }
  .stat-card--green .stat-card__icon { background: #dcfce7; }
  .stat-card--amber .stat-card__icon { background: #fef3c7; }

  .stat-card__value {
    font-family: 'Sora', sans-serif;
    font-size: 34px;
    font-weight: 700;
    line-height: 1;
    color: var(--text-primary);
    margin-bottom: 4px;
  }
  .stat-card__sub {
    font-size: 13px;
    color: var(--text-muted);
    font-weight: 400;
  }
  .stat-card__arrow {
    position: absolute;
    bottom: 18px; right: 22px;
    font-size: 18px;
    color: var(--card-accent, var(--primary));
    opacity: 0;
    transition: opacity 0.2s, transform 0.2s;
    transform: translateX(-4px);
  }
  .stat-card:hover .stat-card__arrow { opacity: 1; transform: translateX(0); }

  /* ── Grid row ── */
  .emp-dash__row {
    display: grid;
    grid-template-columns: 1.6fr 1fr;
    gap: 18px;
    margin-bottom: 28px;
  }
  @media (max-width: 860px) {
    .emp-dash__row { grid-template-columns: 1fr; }
  }

  /* ── Panel base ── */
  .panel {
    background: var(--surface);
    border-radius: var(--radius);
    border: 1px solid var(--border);
    box-shadow: var(--shadow-sm);
    padding: 24px;
  }
  .panel__title {
    font-family: 'Sora', sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 20px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .panel__title-dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    background: var(--primary);
    flex-shrink: 0;
  }

  /* ── Leave Summary ── */
  .leave-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid var(--border);
    font-size: 14px;
    color: var(--text-secondary);
  }
  .leave-row:last-child { border-bottom: none; }
  .leave-row--total {
    margin-top: 8px;
    padding-top: 12px;
    border-top: 2px solid var(--border);
    border-bottom: none;
    font-weight: 700;
    color: var(--text-primary);
    font-size: 15px;
  }
  .leave-badge {
    display: inline-block;
    background: var(--primary-light);
    color: var(--primary);
    font-size: 12px;
    font-weight: 600;
    border-radius: 6px;
    padding: 2px 8px;
    margin-left: 6px;
  }
  .leave-value {
    font-family: 'Sora', sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: var(--text-primary);
  }

  /* ── Progress bar inside leave rows ── */
  .leave-bar-wrap {
    flex: 1;
    margin: 0 14px;
    height: 5px;
    border-radius: 99px;
    background: var(--surface2);
    overflow: hidden;
  }
  .leave-bar-fill {
    height: 100%;
    border-radius: 99px;
    background: linear-gradient(90deg, var(--primary), var(--accent-teal));
    transition: width 0.6s ease;
  }

  /* ── Filter + Bar chart panel ── */
  .chart-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 10px;
  }
  .filter-select {
    font-family: 'DM Sans', sans-serif;
    padding: 7px 14px;
    border-radius: 10px;
    border: 1px solid var(--border);
    background: var(--surface2);
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
    cursor: pointer;
    outline: none;
    transition: border-color 0.2s;
  }
  .filter-select:focus { border-color: var(--primary); }

  /* ── Tooltip ── */
  .custom-tooltip {
    background: var(--text-primary);
    color: #fff;
    border-radius: 10px;
    padding: 10px 14px;
    font-size: 13px;
    line-height: 1.6;
    box-shadow: var(--shadow-md);
  }

  /* ── Empty state ── */
  .empty-state {
    text-align: center;
    padding: 30px 0;
    color: var(--text-muted);
    font-size: 14px;
  }
`;

/* ─── Custom tooltip ──────────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="custom-tooltip">
      <strong style={{ display: "block", marginBottom: 4 }}>{label}</strong>
      {payload.map((p) => (
        <div key={p.name}>
          {p.name}: <strong>{p.value}h</strong>
        </div>
      ))}
    </div>
  );
};

/* ─── Stat Card ───────────────────────────────────────────────── */
const StatCard = ({ label, value, sub, icon, color, path }) => {
  const navigate = useNavigate();
  return (
    <div
      className={`stat-card stat-card--${color}`}
      onClick={() => path && navigate(path)}
      role={path ? "button" : undefined}
      tabIndex={path ? 0 : undefined}
    >
      <div className="stat-card__top">
        <span className="stat-card__label">{label}</span>
        <div className="stat-card__icon">{icon}</div>
      </div>
      <div className="stat-card__value">{value}</div>
      <div className="stat-card__sub">{sub}</div>
      {path && <span className="stat-card__arrow">→</span>}
    </div>
  );
};

/* ─── Main Component ──────────────────────────────────────────── */
const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ name: "Employee" });
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);
  const [filter, setFilter] = useState("thisWeek");
  const [worklogs, setWorklogs] = useState([]);
  const [pendingTasks, setPendingTasks] = useState(0);
  const [pendingLeaves, setPendingLeaves] = useState(0);
  const [remainingLeaves, setRemainingLeaves] = useState(0);
  const [remainingLeaveDetails, setRemainingLeaveDetails] = useState([]);
  const [docCount, setDocCount] = useState(0);
  const [leaveSummary, setLeaveSummary] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [financialYear, setFinancialYear] = useState("");
  const userId = localStorage.getItem("employeeId");

  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  /* ── Fetch financial year ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${API_BASE_URL}/api/company-info`, {
      headers: { Authorization: "Bearer " + token },
    })
      .then(res => res.json())
      .then(data => {
        const company = Array.isArray(data) ? data[0] : data;
        if (company?.financialYear) setFinancialYear(company.financialYear);
      })
      .catch(err => console.error("Error fetching company info:", err));
  }, []);

  /* ── Core data fetching ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    const employeeId = localStorage.getItem("employeeId");
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    const status = localStorage.getItem("status") || "";
    const userId = localStorage.getItem("userId");

    if (!token || status.toUpperCase() !== "ACTIVE" || !roles.includes("ROLE_USER")) {
      navigate("/");
      return;
    }

    fetch(`${API_BASE_URL}/api/users/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => setProfile({ name: data.name || "Employee" }))
      .catch(() => setProfile({ name: "Employee" }));

    fetch(`${API_BASE_URL}/api/worklogs/employee/${employeeId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => {
        setWorklogs(data);
        setChartData(processWorklogData(data, filter));
        setLoading(false);
      });

    fetch(`${API_BASE_URL}/api/tasks/employee/${employeeId}/pending-count`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => setPendingTasks(data.pendingCount || 0));

    fetch(`${API_BASE_URL}/api/uploaddocuments/mydocuments/count`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => setDocCount(data.documentCount || 0));
  }, [navigate]);

  /* ── Pending leaves (after FY ready) ── */
  useEffect(() => {
    const fetchPendingLeaveCount = async () => {
      const token = localStorage.getItem("token");
      const employeeId = localStorage.getItem("employeeId");
      if (!token || !employeeId || !financialYear) return;
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/leave-transactions/employee/${employeeId}/avail/pending-count?financialYear=${financialYear}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error("Failed to fetch pending leave count");
        const count = await res.json();
        setPendingLeaves(count);
      } catch (err) {
        console.error("Error fetching pending leave count:", err);
      }
    };
    fetchPendingLeaveCount();
  }, [financialYear]);

  /* ── Leave summary ── */
  const fetchLeaveSummary = async () => {
    const token = localStorage.getItem("token");
    if (!token || !financialYear) return;
    try {
      const [typesRes, summaryRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/leave-types/all`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE_URL}/api/leave-transactions/summary/${userId}`, {
          params: { financialYear },
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const summaryData = Array.isArray(summaryRes.data) ? summaryRes.data : [];
      setLeaveTypes(typesRes.data);
      setLeaveSummary(summaryData);
      setRemainingLeaveDetails(summaryData);
      setRemainingLeaves(summaryData.reduce((s, i) => s + (i.remaining || 0), 0));
    } catch (err) {
      console.error("Error fetching leave summary:", err);
    }
  };

  useEffect(() => {
    if (financialYear) fetchLeaveSummary();
  }, [financialYear]);

  /* ── Chart filter update ── */
  useEffect(() => {
    if (worklogs.length > 0) setChartData(processWorklogData(worklogs, filter));
  }, [filter, worklogs]);

  /* ── Worklog processor ── */
  const processWorklogData = (worklogs, filterType) => {
    const now = new Date();
    let range;
    if (filterType === "thisWeek") {
      range = { start: startOfWeek(now), end: endOfWeek(now) };
    } else if (filterType === "lastWeek") {
      range = { start: startOfWeek(subWeeks(now, 1)), end: endOfWeek(subWeeks(now, 1)) };
    } else {
      range = { start: startOfMonth(now), end: endOfMonth(now) };
    }
    const dayMap = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const summary = {};
    worklogs.forEach(log => {
      const logDate = parseISO(log.date);
      if (!isWithinInterval(logDate, range)) return;
      const day = dayMap[logDate.getDay()];
      if (!summary[day]) summary[day] = { name: day, Tasks: 0, Billable: 0, NonBillable: 0 };
      summary[day].Tasks += 1;
      if (log.billingType === "billable") summary[day].Billable += log.hours;
      else summary[day].NonBillable += log.hours;
    });
    const ordered = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return ordered.map(day => summary[day] || { name: day, Tasks: 0, Billable: 0, NonBillable: 0 });
  };

  if (loading) return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      minHeight: "100vh", background: "#f0f4ff",
      fontFamily: "'DM Sans', sans-serif", color: "#4f6ef7", fontSize: 16,
    }}>
      <div style={{ textAlign: "center" }}>
        <div style={{
          width: 48, height: 48, borderRadius: "50%",
          border: "4px solid #eef1ff", borderTop: "4px solid #4f6ef7",
          animation: "spin 0.8s linear infinite", margin: "0 auto 16px",
        }} />
        Loading your dashboard…
      </div>
    </div>
  );

  /* ── Pie data ── */
  const overviewData = [
    { name: "Tasks Pending", value: pendingTasks },
    { name: "Leave Requests", value: pendingLeaves },
    { name: "Remaining Leaves", value: remainingLeaves },
    { name: "Documents", value: docCount },
  ];
  const PIE_COLORS = ["#4f6ef7", "#f25f7e", "#0dcfb4", "#f5a623"];

  /* ── Leave bar max ── */
  const maxLeaveAlloc = leaveSummary.reduce(
    (s, i) => s + ((i.allocated || i.remaining + i.applied) || 0), 0
  ) || 1;

  return (
    <>
      <style>{STYLE}</style>
      <TermsConditionsPopup />
      <div className="emp-dash">
        <div style={{ position: "fixed", top: 0, width: "100%", zIndex: 9999 }}>
          <EmployeeNavbar />
        </div>

        <div className="emp-dash__scroll">
          <div className="emp-dash__inner">

          {/* Header */}
          <div className="emp-dash__header">
            <div className="emp-dash__greeting">
              <h2 className="emp-dash__title">
                Hey, <span>{profile.name.split(" ")[0]}</span> 👋
              </h2>
              <p className="emp-dash__greeting-sub">
                Here's what's happening with your workspace today.
              </p>
            </div>
            <div className="emp-dash__date-badge">📅 {today}</div>
          </div>

          {/* Stat Cards */}
          <div className="emp-dash__cards">
            <StatCard
              label="My Tasks"
              value={pendingTasks}
              sub="pending right now"
              icon={<FaTasks size={20} color="#4f6ef7" />}
              color="blue"
              path="/viewtasks"
            />
            <StatCard
              label="Leave Requests"
              value={pendingLeaves}
              sub="awaiting approval"
              icon={<FaClock size={20} color="#0dcfb4" />}
              color="teal"
              path="/allleaves"
            />
            <StatCard
              label="Remaining Leaves"
              value={remainingLeaves}
              sub="days available"
              icon={<FaStopwatch size={20} color="#22c55e" />}
              color="green"
            />
            <StatCard
              label="Documents"
              value={docCount}
              sub="uploaded files"
              icon={<FaFileAlt size={20} color="#f5a623" />}
              color="amber"
              path="/viewdocuments"
            />
          </div>

          {/* Overview + Leave Summary row */}
          <div className="emp-dash__row">

            {/* Pie chart */}
            <div className="panel">
              <h3 className="panel__title">
                <span className="panel__title-dot" />
                Overview
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={overviewData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={60}
                    outerRadius={110}
                    paddingAngle={3}
                    strokeWidth={0}
                  >
                    {overviewData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i]} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) =>
                      active && payload?.length ? (
                        <div className="custom-tooltip">
                          <strong>{payload[0].name}</strong>
                          <div>{payload[0].value}</div>
                        </div>
                      ) : null
                    }
                  />
                  <Legend
                    iconType="circle"
                    iconSize={10}
                    formatter={(val) => (
                      <span style={{ fontSize: 13, color: "#5a617d" }}>{val}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Leave Summary */}
            <div className="panel" style={{ display: "flex", flexDirection: "column" }}>
              <h3 className="panel__title">
                <span className="panel__title-dot" style={{ background: "#0dcfb4" }} />
                Leave Summary
              </h3>

              {leaveSummary.length === 0 ? (
                <div className="empty-state">No leave data for this period.</div>
              ) : (
                <>
                  {leaveSummary.map((item, idx) => {
                    const alloc = (item.allocated || item.remaining + item.applied) || 0;
                    const pct = alloc > 0 ? Math.round((item.remaining / alloc) * 100) : 0;
                    return (
                      <div key={idx} className="leave-row">
                        <span style={{ flex: "0 0 auto", maxWidth: 110, fontSize: 13 }}>
                          {item.leaveType}
                        </span>
                        <div className="leave-bar-wrap">
                          <div className="leave-bar-fill" style={{ width: `${pct}%` }} />
                        </div>
                        <div style={{ textAlign: "right", flex: "0 0 auto" }}>
                          <span className="leave-value">{item.remaining}</span>
                          <span className="leave-badge">−{item.applied}</span>
                        </div>
                      </div>
                    );
                  })}
                  <div className="leave-row leave-row--total">
                    <span>Total</span>
                    <div>
                      <span className="leave-value">
                        {leaveSummary.reduce((s, i) => s + i.remaining, 0)}
                      </span>
                      <span className="leave-badge">
                        −{leaveSummary.reduce((s, i) => s + i.applied, 0)}
                      </span>
                    </div>
                  </div>
                </>
              )}

              <p style={{ fontSize: 11, color: "#97a0bb", marginTop: "auto", paddingTop: 16 }}>
                Remaining <span className="leave-badge" style={{ background: "#eef1ff", color: "#4f6ef7" }}>−Applied</span>
              </p>
            </div>
          </div>

          {/* Bar Chart */}
          <div className="panel">
            <div className="chart-header">
              <h3 className="panel__title" style={{ margin: 0 }}>
                <span className="panel__title-dot" style={{ background: "#f5a623" }} />
                Weekly Hours Log
              </h3>
              <select
                className="filter-select"
                value={filter}
                onChange={e => setFilter(e.target.value)}
              >
                <option value="thisWeek">This Week</option>
                <option value="lastWeek">Last Week</option>
              </select>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} barGap={6}>
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#97a0bb", fontSize: 12 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#97a0bb", fontSize: 12 }}
                  unit="h"
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(79,110,247,0.05)", radius: 8 }} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  formatter={(val) => (
                    <span style={{ fontSize: 13, color: "#5a617d" }}>{val}</span>
                  )}
                />
                <Bar dataKey="Billable" fill="#4f6ef7" radius={[6, 6, 0, 0]} barSize={28} />
                <Bar dataKey="NonBillable" fill="#f25f7e" radius={[6, 6, 0, 0]} barSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>

        </div>
        </div>
      </div>

      {/* Spinner keyframe */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

export default EmployeeDashboard;